import { useFormContext } from "react-hook-form";
import { OfferYachtForm } from "../offer-yacht.schema";
import { Amenity, YachtDetails } from "@/src/app/Yachts/model";
import { useEffect, useState } from "react";
import { calculateDaysStarted, calculateWeeksStarted } from "./ChangeYachtDetailsModal";
import { useOffersStore } from "@/src/stores/offersStore";
import { calculateCurrencyPrice } from "@/src/lib/utils";

export type AmenityConfig = OfferYachtForm["amenitiesDetails"][number];

export type UseExtrasOutput = {
  amenities: Amenity[];
  amenitiesDetails: AmenityConfig[];
};
export type UseExtrasInput = {
  defaultAmenitiesDetails?: AmenityConfig[];
  form: ReturnType<typeof useFormContext<OfferYachtForm>>;
  yacht: YachtDetails | null;
  startDate?: string;
  endDate?: string;
  regionNames?: string;
  startLocationNames?: string;
  endLocationNames?: string;
  guests: number;
  offerCurrencyCode?: string;
  clientPrice: number;
  listPrice: number;
};
export function useExtras({
  defaultAmenitiesDetails,
  form,
  startDate,
  endDate,
  guests,
  startLocationNames,
  regionNames,
  endLocationNames,
  yacht,
  offerCurrencyCode,
  clientPrice,
  listPrice,
}: UseExtrasInput): UseExtrasOutput {
  let days = 1,
    weeks = 1;
  if (startDate && endDate) {
    days = calculateDaysStarted(startDate, endDate);
    weeks = calculateWeeksStarted(startDate, endDate);
  }
  const [amenities, setAmenities] = useState<UseExtrasOutput>({
    amenities: [],
    amenitiesDetails: [],
  });
  const { currencies } = useOffersStore();
  const offerCurrency = currencies?.find((c) => c.code === (offerCurrencyCode ?? "EUR"));
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    // Use the entered guest count for pax-restricted extras; fall back to the
    // yacht's max capacity when none is selected.
    const people = guests && guests > 0 ? guests : (yacht?.maxPeople ?? guests);
    const amenities = yacht?.amenities?.map((am) => ({ ...am, paidLocally: am.paid_locally })) ?? [];
    const amenitiesDetails =
      defaultAmenitiesDetails ??
      amenities
        ?.filter((am) => !am.included || am.isCustomAmenity)
        .filter(
          (am) =>
            !!(function (region, startLocation, endLocation, duration, charterStart, charterEnd, people) {
              const checkName = (a: string[], v: string) => v.split("\n").some((n) => a.includes(n));
              const res = eval(am.conditionAvailable);
              return res;
            })(
              regionNames ?? "",
              startLocationNames ?? "",
              endLocationNames ?? "",
              days,
              startDate ?? today,
              endDate ?? today,
              people,
            ),
        )
        .map((am) => {
          return {
            paidLocally: am.paidLocally,
            price: (function (location, duration, clientPrice, listPrice, people, varies) {
              const checkName = (a: string[], v: string) => v.split("\n").some((n) => a.includes(n));
              const fn = eval(
                `(startLocation, duration, people, clientPrice, listPrice, varies) => {\n\n${am.formula}\n\n};`,
              );
              const res = fn(location, duration, people, clientPrice, listPrice, varies);
              const amenityCurrency = currencies?.find((c) => c.code === (am.currency?.code ?? "EUR"));
              // if amenity is paid in advance recalculate the currency if needed
              if (!am.paidLocally && offerCurrency?.code !== amenityCurrency?.code) {
                return calculateCurrencyPrice(offerCurrency!, amenityCurrency!, res || 0);
              } else {
                return res || 0;
              }
            })(startLocationNames, days, clientPrice, listPrice, people, 1),
            currency: am.paidLocally ? "EUR" : (offerCurrencyCode ?? "EUR"),
            currencyId: am.currency?.id,
            checked: am.obligatory,
            text: am.text,
            description: am.description,
            obligatory: am.obligatory,
            id: am.id,
            provider: am?.provider,
            number: am?.number,
            formula: am?.formula,
          };
        }) ??
      [];

    (window as any)["form"] = form;

    form.setValue("amenitiesDetails", amenitiesDetails, {
      shouldTouch: true,
      shouldDirty: true,
    });

    setAmenities({
      amenities,
      amenitiesDetails,
    });
  }, [
    days,
    weeks,
    regionNames,
    startLocationNames,
    endLocationNames,
    guests,
    yacht,
    offerCurrencyCode,
    form,
    startDate,
    endDate,
    clientPrice,
    listPrice,
    defaultAmenitiesDetails,
  ]);

  const amenitiesDetails = form.watch("amenitiesDetails");

  return {
    amenities: amenities.amenities,
    amenitiesDetails,
  };
}
